<template>
  <AlertMessage
    :message="alertMessage"
    :alertType="alertType ? alertType : 'empty'"
    @close="alertClose"
  />

  <div class="bg-gray-50 px-4 py-6 sm:px-6">
    <div class="max-w-7xl mx-auto">
      <!-- Header Section -->
      <div
        class="mb-8 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between"
      >
        <div>
          <h1 class="text-2xl font-semibold text-neutral-text mb-2">
            {{ questionnaire?.name || "Loading Poll Results..." }}
          </h1>
          <div class="flex items-center gap-2">
            <span class="text-neutral-light">Responses</span>
            <span
              class="bg-primary/10 text-primary-neutral text-sm font-medium px-2.5 py-0.5 rounded-full"
            >
              {{ numberOfQuestions }} /
              {{ questionnaire?.numberOfTargetUsers || 1 }}
            </span>
          </div>
        </div>
        <div class="flex w-full flex-wrap gap-3 lg:w-auto lg:justify-end">
          <ActionButton
            action="back"
            class="w-full sm:w-auto"
            @click="$router.go(-1)"
          >
            Back
          </ActionButton>
        </div>
      </div>

      <!-- Tab Navigation -->
      <div class="mb-6">
        <div class="border-b border-gray-200">
          <nav
            class="-mb-px flex gap-6 overflow-x-auto pb-1 text-sm"
            aria-label="Poll result tabs"
          >
            <button
              @click="activeTab = 'summary'"
              :class="[
                'py-2 px-1 border-b-2 font-medium transition-colors',
                activeTab === 'summary'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              ]"
            >
              Summary
            </button>
            <button
              v-if="questionnaire?.isAnonymous === false"
              @click="activeTab = 'individual'"
              :class="[
                'py-2 px-1 border-b-2 font-medium transition-colors',
                activeTab === 'individual'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              ]"
            >
              Individual Responses
            </button>
          </nav>
        </div>
      </div>

      <!-- Summary Tab Content -->
      <div
        v-if="activeTab === 'summary'"
        class="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        <!-- Iterate through each question in the questionnaire -->
        <div
          v-for="(question, questionId) in questionnaire?.questions || {}"
          :key="questionId"
          class="bg-white p-6 shadow-sm rounded-lg border border-gray-200"
        >
          <!-- Display chart for text-type questions -->
          <div v-if="question.type === 'text'">
            <h3 class="text-lg font-semibold mb-4 text-gray-900">
              {{ question.title }}
            </h3>
            <div class="w-full" v-if="chartSeries[questionId]?.length">
              <ol class="space-y-2">
                <li
                  class="text-sm text-gray-600 leading-relaxed"
                  v-for="(item, index) in chartOptions[questionId].xaxis
                    .categories"
                  :key="index"
                >
                  <span class="font-medium text-gray-900 mr-2"
                    >{{ index + 1 }}.</span
                  >
                  <span>{{ item }}</span>
                </li>
              </ol>
            </div>
            <div v-else class="text-sm text-gray-500 italic">
              No responses yet
            </div>
          </div>

          <!-- Display chart for radio-type questions -->
          <div v-if="question.type === 'radio'">
            <h3 class="text-lg font-semibold mb-4 text-gray-900">
              {{ question.title }}
            </h3>
            <div v-if="chartSeries[questionId]?.length">
              <apexchart
                type="pie"
                :options="chartOptions[questionId] || defaultRadioChartOptions"
                :series="chartSeries[questionId] || defaultRadioChartSeries"
                class="chart-width"
              ></apexchart>
            </div>
            <div v-else class="text-sm text-gray-500 italic">
              No responses yet
            </div>
          </div>
        </div>
      </div>

      <!-- Individual Tab Content -->
      <div
        v-if="activeTab === 'individual'"
        class="bg-white p-6 shadow-sm rounded-lg border border-gray-200"
      >
        <!-- User Selection -->
        <div class="mb-6">
          <label
            for="user-select"
            class="block text-sm font-medium text-gray-700 mb-2"
          >
            Select a User:
          </label>
          <select
            id="user-select"
            v-model="selectedUserIndex"
            class="block w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-primary"
          >
            <option value="" disabled>Choose a user...</option>
            <option
              v-for="(response, index) in questionnaireData || []"
              :key="index"
              :value="index"
            >
              {{ response.firstName }} {{ response.lastName }}
            </option>
          </select>
        </div>

        <!-- Display the selected user's responses -->
        <div v-if="selectedUserIndex !== null && selectedUserIndex !== ''">
          <div class="border-b border-gray-200 pb-4 mb-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-2">
              Response from
              {{ questionnaireData[selectedUserIndex]?.firstName }}
              {{ questionnaireData[selectedUserIndex]?.lastName }}
            </h3>
            <div
              class="flex flex-col sm:flex-row sm:gap-6 gap-1 text-sm text-gray-600"
            >
              <div>
                <span class="font-medium">Email:</span>
                {{ questionnaireData[selectedUserIndex]?.email }}
              </div>
              <div>
                <span class="font-medium">Answer Date:</span>
                {{ questionnaireData[selectedUserIndex]?.answerDate }}
              </div>
            </div>
          </div>

          <div class="space-y-4">
            <div
              v-for="answer in questionnaireData[selectedUserIndex]?.answers
                ?.answers || []"
              :key="answer.answerTitle"
              class="border-l-4 border-primary/20 pl-4 py-2"
            >
              <p class="font-medium text-gray-900 mb-1">
                {{ answer.questionTitle }}
              </p>
              <p class="text-gray-600">{{ answer.answerTitle }}</p>
            </div>
          </div>
        </div>

        <div v-else class="text-center py-8">
          <p class="text-gray-500">
            Please select a user to view their response.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApexCharts from "vue3-apexcharts";
import { getQuestionnaireAndResponses } from "@/services/questionnaire";
import { getQuestionnaireById } from "@/services/questionnaire";
import AlertMessage from "@/components/AlertMessage.vue";
import ActionButton from "@/components/ui/ActionButton.vue";

export default {
  name: "PollResult",
  components: {
    apexchart: ApexCharts,
    AlertMessage,
    ActionButton,
  },
  props: {
    pollId: {
      type: [String, Number],
      required: true,
    },
  },
  inheritAttrs: false,
  data() {
    return {
      questionnaire: {},
      questionnaireData: [],
      chartOptions: {},
      chartSeries: {},
      activeTab: "summary",
      alertMessage: "",
      alertType: "",
      selectedUserIndex: "",
      numberOfQuestions: 1,
      defaultTextChartOptions: {
        chart: {
          type: "bar",
          stacked: false,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "50%",
          },
        },
        xaxis: {
          categories: [],
        },
        legend: {
          position: "right",
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      },
      defaultTextChartSeries: [],
      defaultRadioChartOptions: {
        chart: {
          type: "pie",
        },
        legend: {
          position: "right",
          width: 250,
          offsetX: -20,
          offsetY: 0,
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      },
      defaultRadioChartSeries: [],
    };
  },
  methods: {
    async getQuestionnairesWithAnswers() {
      try {
        if (this.pollId) {
          const id =
            typeof this.pollId === "string"
              ? parseInt(this.pollId, 10)
              : this.pollId;
          this.questionnaireData = await getQuestionnaireAndResponses(id);
          this.numberOfQuestions = this.questionnaireData.length;
          this.updateChartData();
        }
      } catch (error) {
        this.alertMessage = "Unable to get poll data. Please try again later.";
        this.alertType = "dismiss";
      }
    },
    async getQuestionnaireById() {
      try {
        if (this.pollId) {
          const id =
            typeof this.pollId === "string"
              ? parseInt(this.pollId, 10)
              : this.pollId;
          this.questionnaire = await getQuestionnaireById(id);
        }
      } catch {
        this.alertMessage =
          "Failed to fetch questionnaire data. Please try again later.";
        this.alertType = "dismiss";
      }
    },
    alertClose() {
      this.alertMessage = "";
      this.alertType = "";
    },
    updateChartData() {
      const questions = this.questionnaire?.questions || {};

      for (const [questionId, question] of Object.entries(questions)) {
        const answers = (
          Array.isArray(this.questionnaireData) ? this.questionnaireData : []
        ).flatMap((response) => {
          return (
            response.answers?.answers
              ?.filter((answer) => {
                const matchesTitle =
                  answer.questionTitle.trim().toLowerCase() ===
                  question.title.trim().toLowerCase();
                const matchesType = answer.questionType === question.type; // Check against question type dynamically

                return matchesTitle && matchesType;
              })
              .map((answer) => answer.answerTitle) || []
          ); // Handle potential undefined
        });
        if (answers.length > 0) {
          const answerCounts = answers.reduce((acc, answer) => {
            acc[answer] = (acc[answer] || 0) + 1;
            return acc;
          }, {});

          if (question.type === "text") {
            const uniqueOptions = [...new Set(answers)];

            // Ensure all unique options have counts, even if zero
            uniqueOptions.forEach((option) => {
              if (!answerCounts[option]) {
                answerCounts[option] = 0;
              }
            });
            this.chartSeries[questionId] = [
              {
                name: question.title,
                data: uniqueOptions.map((option) => answerCounts[option]),
              },
            ];
            this.chartOptions[questionId] = {
              ...this.defaultTextChartOptions,
              xaxis: {
                categories: uniqueOptions,
              },
            };
          } else if (question.type === "radio") {
            this.chartSeries[questionId] = Object.values(answerCounts);

            this.chartOptions[questionId] = {
              ...this.defaultRadioChartOptions,
              labels: Object.keys(answerCounts),
            };
          }
        }
      }
    },
  },
  watch: {
    pollId: {
      immediate: true,
      async handler(newPollId) {
        if (newPollId) {
          await this.getQuestionnaireById();
          await this.getQuestionnairesWithAnswers();
        }
      },
    },
  },
};
</script>

<style scoped>
#user-select {
  max-width: 12rem;
  font-size: 0.875rem;
}
.chart-width {
  width: 75%;
}
</style>
